import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { bytesFor } from './documentStore';

// Vite bundles the worker as its own asset and hands us the resolved URL.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).href;

const THUMBNAIL_WIDTH = 240;
/** Rendering every page at once starves the UI thread on large documents. */
const MAX_CONCURRENT_RENDERS = 4;

const documentCache = new Map<string, Promise<PDFDocumentProxy>>();
const thumbnailCache = new Map<string, string>();

let active = 0;
const queue: Array<() => void> = [];

function acquireSlot(): Promise<void> {
  if (active < MAX_CONCURRENT_RENDERS) {
    active += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => queue.push(resolve));
}

function releaseSlot(): void {
  const next = queue.shift();
  if (next) {
    next();
  } else {
    active -= 1;
  }
}

function loadDocument(docId: string): Promise<PDFDocumentProxy> {
  let pending = documentCache.get(docId);
  if (!pending) {
    const bytes = bytesFor(docId);
    if (!bytes) return Promise.reject(new Error('Document is no longer available.'));

    // .slice() is not optional. pdf.js transfers the buffer it is handed to its
    // worker, which detaches it — the original would become a zero-length view
    // and every later read of this document, including the export, would fail.
    pending = pdfjs.getDocument({
      data: bytes.slice(),
      // Page previews do not need embedded fonts to be pixel-perfect.
      disableFontFace: false,
    }).promise;
    documentCache.set(docId, pending);
    pending.catch(() => documentCache.delete(docId));
  }
  return pending;
}

/**
 * Renders one page to a PNG data URL, cached per page.
 *
 * Rotation is intentionally not baked in — the UI rotates the cached image with
 * a CSS transform, so rotating a page is instant and never re-renders.
 */
export async function renderThumbnail(docId: string, pageIndex: number): Promise<string> {
  const key = `${docId}:${pageIndex}`;
  const cached = thumbnailCache.get(key);
  if (cached) return cached;

  await acquireSlot();
  try {
    const pdf = await loadDocument(docId);
    const page = await pdf.getPage(pageIndex + 1);
    const baseViewport = page.getViewport({ scale: 1 });
    const scale = THUMBNAIL_WIDTH / baseViewport.width;
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas is unavailable in this browser.');

    // Pages with transparent backgrounds would otherwise render black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;
    page.cleanup();

    const dataUrl = canvas.toDataURL('image/png');
    thumbnailCache.set(key, dataUrl);
    return dataUrl;
  } finally {
    releaseSlot();
  }
}

/**
 * Renders one page large enough to read, for the preview modal.
 *
 * Kept apart from the thumbnail cache and deliberately small: these are roughly
 * thirty times the pixels of a thumbnail, so holding every page someone clicked
 * through would grow without bound. Five covers paging back and forth.
 */
const PREVIEW_WIDTH = 1400;
const MAX_PREVIEWS = 5;
const previewCache = new Map<string, PreviewRender>();

export interface PreviewRender {
  url: string;
  /** The page's own size in points, before any rotation is applied. */
  widthPt: number;
  heightPt: number;
}

export async function renderPreview(docId: string, pageIndex: number): Promise<PreviewRender> {
  const key = `${docId}:${pageIndex}`;
  const cached = previewCache.get(key);
  if (cached) {
    // Refresh its position so the least recently viewed is the one dropped.
    previewCache.delete(key);
    previewCache.set(key, cached);
    return cached;
  }

  await acquireSlot();
  try {
    const pdf = await loadDocument(docId);
    const page = await pdf.getPage(pageIndex + 1);
    const base = page.getViewport({ scale: 1 });
    // Never upscale past the page's own size on screen: a 2x device pixel ratio
    // is worth honoring, beyond that it is memory for nothing.
    const scale = Math.min(PREVIEW_WIDTH / base.width, (window.devicePixelRatio || 1) * 2);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas is unavailable in this browser.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);

    await page.render({ canvasContext: context, viewport }).promise;
    page.cleanup();

    const render: PreviewRender = {
      url: canvas.toDataURL('image/png'),
      widthPt: base.width,
      heightPt: base.height,
    };
    previewCache.set(key, render);
    if (previewCache.size > MAX_PREVIEWS) {
      const oldest = previewCache.keys().next().value;
      if (oldest !== undefined) previewCache.delete(oldest);
    }
    return render;
  } finally {
    releaseSlot();
  }
}

/**
 * Renders the first page of a PDF held in memory.
 *
 * Used by the export preview, which assembles a real one-page PDF with the
 * stamps applied and shows it — so what you see is the actual output rather
 * than a drawing that approximates it and can drift from the truth.
 */
export async function renderPdfBytes(bytes: Uint8Array, targetWidth = 1000): Promise<string> {
  await acquireSlot();
  let pdf: PDFDocumentProxy | null = null;
  try {
    // A copy, because pdf.js transfers the buffer to its worker and detaches it.
    pdf = await pdfjs.getDocument({ data: bytes.slice() }).promise;
    const page = await pdf.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const scale = Math.min(targetWidth / base.width, 2);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas is unavailable in this browser.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport }).promise;
    page.cleanup();
    return canvas.toDataURL('image/png');
  } finally {
    // Not cached: it changes with every adjustment, so holding it would only
    // grow memory for something already superseded.
    void pdf?.destroy().catch(() => undefined);
    releaseSlot();
  }
}

/** Drops cached render data for a document that is no longer in the workspace. */
export function forgetDocument(docId: string): void {
  void documentCache
    .get(docId)
    ?.then((pdf) => pdf.destroy())
    .catch(() => undefined);
  documentCache.delete(docId);
  for (const key of thumbnailCache.keys()) {
    if (key.startsWith(`${docId}:`)) thumbnailCache.delete(key);
  }
  for (const key of previewCache.keys()) {
    if (key.startsWith(`${docId}:`)) previewCache.delete(key);
  }
}

export function forgetAll(): void {
  for (const docId of [...documentCache.keys()]) forgetDocument(docId);
  thumbnailCache.clear();
  previewCache.clear();
}
